import { useState, useEffect } from "react";
import axios from "axios";
import { API_BASE } from "../../config";
import { defaultCustomizationState } from "./customizationConstants";

export function useCustomizationState({ user }) {
  const isAdmin = user?.role === "admin";
  const urlParams = new URLSearchParams(window.location.search);
  const urlClientId = urlParams.get("clientId");

  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(urlClientId || "");
  const [bots, setBots] = useState([]);
  const [selectedBot, setSelectedBot] = useState("");
  const [customization, setCustomization] = useState(defaultCustomizationState);
  const [message, setMessage] = useState({ type: "", text: "" });

  const effectiveClientId = isAdmin ? selectedClient : user?.clientId || "";

  useEffect(() => {
    if (isAdmin) {
      axios
        .get(`${API_BASE}/api/clients`)
        .then((response) => setClients(response.data))
        .catch((error) => console.error("Erro ao buscar clientes:", error));
    }
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin && user?.clientId) {
      setSelectedClient(user.clientId);
    }
  }, [isAdmin, user?.clientId]);

  useEffect(() => {
    if (!effectiveClientId) {
      setBots([]);
      setSelectedBot("");
      return;
    }
    axios
      .get(`${API_BASE}/api/bots`, { params: { clientId: effectiveClientId } })
      .then((response) => {
        const list = Array.isArray(response.data) ? response.data : [];
        setBots(list);
        setSelectedBot((prev) => {
          const stillValid = list.some((b) => (b.id || b._id) === prev);
          return stillValid ? prev : list[0] ? list[0].id || list[0]._id : "";
        });
      })
      .catch((error) => {
        console.error("Erro ao buscar bots:", error);
        setBots([]);
        setSelectedBot("");
      });
  }, [effectiveClientId]);

  useEffect(() => {
    if (selectedBot) {
      axios
        .get(`${API_BASE}/api/customization/${selectedBot}`)
        .then((response) => {
          if (response.data) {
            setCustomization((prev) => ({
              ...prev,
              ...response.data,
              colors: {
                ...prev.colors,
                ...response.data.colors,
              },
            }));
          }
        })
        .catch((error) => {
          if (error.response && error.response.status === 404) {
            console.warn("Nenhuma customização encontrada. Carregando valores padrão.");
          } else {
            console.error("Erro ao buscar customização:", error);
            setMessage({
              type: "error",
              text: "Erro ao carregar a customização. Tente novamente.",
            });
          }
        });
    }
  }, [selectedBot]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (["primary", "secondary", "botBubble", "userBubble"].includes(name)) {
      setCustomization((prev) => ({
        ...prev,
        colors: { ...prev.colors, [name]: value },
      }));
    } else {
      setCustomization((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!selectedBot) {
      setMessage({
        type: "error",
        text: "Por favor, selecione um bot para personalizar.",
      });
      return;
    }

    const body = {
      botId: selectedBot,
      colors: customization.colors,
      logo: customization.logo,
      favicon: customization.favicon,
      headerText: customization.headerText,
      welcomeMessage: customization.welcomeMessage,
      footerText: customization.footerText,
    };
    if (isAdmin) {
      body.llmProvider = customization.llmProvider;
      body.llmModel = customization.llmModel;
    }

    axios
      .post(`${API_BASE}/api/customization`, body)
      .then(() => {
        setMessage({
          type: "success",
          text: "Customização salva com sucesso!",
        });

        setTimeout(() => {
          setMessage({ type: "", text: "" });
        }, 3000);
      })
      .catch((error) => {
        console.error("Erro ao salvar customização:", error);
        setMessage({
          type: "error",
          text: "Erro ao salvar customização. Tente novamente.",
        });
      });
  };

  return {
    isAdmin,
    clients,
    selectedClient,
    setSelectedClient,
    bots,
    selectedBot,
    setSelectedBot,
    effectiveClientId,
    customization,
    setCustomization,
    message,
    handleChange,
    handleSubmit,
  };
}
